import { IACError } from '../iacError';

/*
 * https://wiki.1and1.org/Ihapps/InterApplicationCommunication40UsecaseSpecs#JSON_Specification
 *
 * @property {number} type - Contains the bitwise flags. Must be an integer.
 * @property {string} sender - Defines the sender name
 * @property {string} usecase - Defines the use case name
 * @property {array}  args - Defines an array with arguments
 */
export interface IACMessageData {
  type: number;
  sender: string;
  usecase: string;
  args: any[];
}

/*
 * IACProtocol
 */
export class IACProtocol {

  /*
   * The IAC raw message data
   */
  private data: IACMessageData = {
    type: 0,
    sender: '',
    usecase: '',
    args: [],
  };

  /*
   * Sets one or more flags.
   * @param {Number} flags -An bitwise flagged integer
   */
  public setFlags(flags: number): void {
    this.data.type |= flags;
  }

  /*
   * Returns the flags.
   *
   * @returns {Number}
   */
  public getFlags(): number {
    return this.data.type;
  }

  /*
   * Unsets a flag. The method finds the bit position automatically.
   *
   * @param {Number} flag -Sets a flag
   * @returns {IACProtocol} -Returns itself
   */
  unsetFlag(flag: number): void {
    let pos = -1;

    while (Math.pow(2, ++pos) !== flag) {
        // write something here pls :D
    }

    this.data.type &= ~(1 << pos);
  }

  /*
   * Sets the name of the sender.
   *
   * @param {string} sender -The name of the sending application
   * @returns {IACProtocol} -Returns itself
   */
  setSender(sender: string): void {
    if (typeof sender !== 'string') {
      throw new IACError('Wrong data type for sender. sender must be a type of string.');
    }

    if (!sender) {
      throw new IACError('The sender cannot be empty.');
    }

    this.data.sender = sender;
  }

  /*
   * Returns the sender.
   *
   * @returns {string} -The name of the sender
   */
  public getSender(): string {
    return this.data.sender;
  }

  /*
   * Sets the name of the usecase.
   *
   * @param {string} name -The name of the usecase
   * @returns {IACProtocol} -Returns itself
   */
  public setName(name: string): void {
    if (typeof name !== 'string') {
      throw new IACError('Wrong data type for name. name must be a type of string.', 'Invalid_Use_case_name');
    }

    if (!name) {
      throw new IACError('The name cannot be empty.', 'Invalid_Use_case_name');
    }

    this.data.usecase = name;
  }

  /*
   * Returns the name.
   *
   * @returns {string} -The name of the usecase
   */
  public getName(): string {
    return this.data.usecase;
  }

  /*
   * Sets the payload.
   *
   * @returns {IACProtocol} -Returns itself
   */
  public setPayload(...args: any[]): void {
    this.data.args = args;
  }

  /*
   * Returns the payload.
   *
   * @returns {array} -Returns the payload
   */
  public getPayload(): any[] {
    return this.data.args;
  }

  /*
   * Returns the raw protocol data.
   *
   * @returns {object} -Returns the raw data
   */
  public getRawData(): IACMessageData {
    return this.data;
  }
}
